import React, { useState, useCallback } from 'react';

interface LoadingButtonProps {
  onClick: () => Promise<void> | void;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'outline-primary' | 'outline-secondary' | 'outline-success' | 'outline-danger' | 'outline-warning';
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  size?: 'sm' | 'md' | 'lg';
  loadingText?: string;
  icon?: React.ReactNode;
  [key: string]: any;
}

const LoadingButton: React.FC<LoadingButtonProps> = ({
  onClick,
  children,
  variant = 'primary',
  className = '',
  disabled = false,
  type = 'button',
  size = 'md',
  loadingText,
  icon,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = useCallback(async () => {
    // Prevent multiple clicks while loading
    if (isLoading || disabled) {
      return;
    }

    setIsLoading(true);
    try {
      await onClick();
    } catch (error) {
      // Error handling is expected to be done in the onClick handler
      // Re-throw to allow parent error handling
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [onClick, isLoading, disabled]);

  // Size classes
  const sizeClasses = {
    sm: 'btn-sm',
    md: '',
    lg: 'btn-lg'
  };

  // Determine button classes
  const buttonClasses = [
    'btn',
    variant.startsWith('outline-') ? `btn-outline-${variant.replace('outline-', '')}` : `btn-${variant}`,
    sizeClasses[size],
    className,
    isLoading ? 'disabled' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Custom styles for primary variant
  const getCustomStyles = () => {
    const baseStyles = {
      position: 'relative' as const,
      cursor: isLoading || disabled ? 'not-allowed' : 'pointer',
      opacity: isLoading || disabled ? 0.6 : 1,
    };

    if (variant === 'primary') {
      return {
        ...baseStyles,
        backgroundColor: '#fd7e14',
        borderColor: '#fd7e14',
        color: '#ffffff',
      };
    }

    if (variant === 'outline-primary') {
      return {
        ...baseStyles,
        borderColor: '#fd7e14',
        color: '#fd7e14',
      };
    }

    return baseStyles;
  };

  return (
    <button
      type={type}
      onClick={handleClick}
      className={buttonClasses}
      disabled={isLoading || disabled}
      style={{
        ...getCustomStyles(),
        ...props.style
      }}
      {...props}
    >
      {isLoading ? (
        <span className="d-flex align-items-center justify-content-center gap-2">
          <span 
            className="spinner-border spinner-border-sm" 
            role="status" 
            aria-hidden="true"
            style={{ width: '1rem', height: '1rem' }}
          />
          {loadingText || children}
        </span>
      ) : (
        <span className="d-flex align-items-center justify-content-center gap-2">
          {icon && <span className="me-1">{icon}</span>}
          {children}
        </span>
      )}
    </button>
  );
};

export default LoadingButton;

